import { Component, TemplateRef, computed, inject, input, linkedSignal } from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { DsmDealOption } from '../../../models/dsm-deal-options-view.models';
import { DsmApsDealContextService } from '../../../services/dsm-aps-deal-context.service';
import { DsmApsAddendumPreviewComponent } from '../dsm-aps-addendum-preview/dsm-aps-addendum-preview.component';
import { DsmApsAddendumOptionsComponent } from '../dsm-aps-addendum-options/dsm-aps-addendum-options.component';

type AddendumRow = DsmDealOption & { isExpanded: boolean };

@Component({
  selector: 'dsm-sign-off-addendum-list',
  standalone: true,
  imports: [NgClass, NgTemplateOutlet, DsmApsAddendumPreviewComponent, DsmApsAddendumOptionsComponent],
  templateUrl: './dsm-sign-off-addendum-list.component.html'
})
export class DsmSignOffAddendumListComponent {
  readonly #dealContext = inject(DsmApsDealContextService);

  readonly addendums = input.required<DsmDealOption[]>();
  readonly expandable = input<boolean>(true);
  readonly showTotal = input<boolean>(true);
  readonly emptyMessage = input<string>('');
  readonly highlightedIds = input<readonly string[]>([]);
  readonly rowActionTemplate = input<TemplateRef<{ $implicit: DsmDealOption }> | null>(null);

  readonly #highlighted = computed(() => new Set(this.highlightedIds()));

  isHighlighted(id: string): boolean {
    return this.#highlighted().has(id);
  }

  readonly rows = linkedSignal<AddendumRow[]>(() =>
    this.addendums().map(addendum => ({ ...addendum, isExpanded: false }) as AddendumRow)
  );

  readonly lotNo = computed(() => this.#dealContext.currentDeal()?.lot?.lotNo ?? null);
  readonly modelId = computed(() => this.#dealContext.currentDeal()?.model?.id ?? '');
  readonly elevationId = computed(() => this.#dealContext.currentDeal()?.elevation?.id ?? '');
  readonly projectName = computed(
    () =>
      this.#dealContext.currentDeal()?.project?.projectName ??
      this.#dealContext.currentDeal()?.project?.projectCode ??
      null
  );
  readonly modelName = computed(() => this.#dealContext.currentDeal()?.model?.modelName ?? null);
  readonly elevationLabel = computed(() => this.#dealContext.currentDeal()?.elevation?.elevationCode ?? null);
  readonly buyerName = computed(() => this.#dealContext.currentDeal()?.prospect?.name ?? null);

  toggleRow(row: AddendumRow): void {
    this.rows.update(rows => rows.map(r => (r.id === row.id ? { ...r, isExpanded: !r.isExpanded } : r)));
  }
}
